import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PersonIcon from '@mui/icons-material/Person';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import { db } from '../../lib/firebase.js';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function AdminContent() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [stories, setStories] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [filteredStories, setFilteredStories] = useState([]);
  const [filteredCharacters, setFilteredCharacters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      const filtered = stories.filter(story =>
        (story.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        story.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredStories(filtered);
    } else {
      const filtered = characters.filter(char =>
        (char.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        char.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCharacters(filtered);
    }
    setPage(0);
  }, [searchQuery, stories, characters, activeTab]);

  const fetchContent = async () => {
    try {
      setLoading(true);

      const [storiesSnapshot, charactersSnapshot] = await Promise.all([
        getDocs(collection(db, 'stories')),
        getDocs(collection(db, 'characters')),
      ]);

      const storiesList = storiesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      const charactersList = charactersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setStories(storiesList);
      setCharacters(charactersList);
      setFilteredStories(storiesList);
      setFilteredCharacters(charactersList);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleViewDetails = () => {
    handleMenuClose();
    setDetailsOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;

    try {
      setDeleting(true);
      const collectionName = activeTab === 0 ? 'stories' : 'characters';
      await deleteDoc(doc(db, collectionName, selectedItem.id));

      if (activeTab === 0) {
        setStories(prev => prev.filter(s => s.id !== selectedItem.id));
        setFilteredStories(prev => prev.filter(s => s.id !== selectedItem.id));
      } else {
        setCharacters(prev => prev.filter(c => c.id !== selectedItem.id));
        setFilteredCharacters(prev => prev.filter(c => c.id !== selectedItem.id));
      }

      setDeleteConfirmOpen(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchQuery('');
    setPage(0);
  };

  const getImageCount = (story) => {
    const pageImages = (story.pages || []).filter(p => p.imageUrl).length;
    const coverImage = story.cover_image_url ? 1 : 0;
    return pageImages + coverImage;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            icon={<AutoStoriesIcon />}
            iconPosition="start"
            label={`Stories (${stories.length})`}
          />
          <Tab
            icon={<PersonIcon />}
            iconPosition="start"
            label={`Characters (${characters.length})`}
          />
        </Tabs>
        <TextField
          size="small"
          placeholder={`Search ${activeTab === 0 ? 'stories' : 'characters'}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {/* Stories Tab */}
      <TabPanel value={activeTab} index={0}>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell align="center">Pages</TableCell>
                  <TableCell align="center">Images</TableCell>
                  <TableCell align="center">Audio</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStories
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((story) => (
                    <TableRow key={story.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {story.cover_image_url ? (
                            <Avatar
                              src={story.cover_image_url}
                              variant="rounded"
                              sx={{ width: 40, height: 40 }}
                            />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}>
                              <AutoStoriesIcon color="action" />
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {story.title || 'Untitled'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {story.themes?.slice(0, 2).join(', ') || 'No themes'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {story.user_id?.substring(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {story.pages?.length || 0}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<ImageIcon sx={{ fontSize: 14 }} />}
                          label={getImageCount(story)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {story.selected_voice ? (
                          <Chip
                            icon={<AudiotrackIcon sx={{ fontSize: 14 }} />}
                            label={story.selected_voice}
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(story.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, story)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredStories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No stories found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredStories.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      </TabPanel>

      {/* Characters Tab */}
      <TabPanel value={activeTab} index={1}>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Character</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Traits</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCharacters
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((char) => (
                    <TableRow key={char.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {char.avatar ? (
                            <Avatar src={char.avatar} sx={{ width: 40, height: 40 }} />
                          ) : (
                            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}>
                              {char.name?.[0]?.toUpperCase() || 'C'}
                            </Avatar>
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {char.name || 'Unnamed'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {char.user_id?.substring(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{char.age || '-'}</TableCell>
                      <TableCell>{char.gender || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(char.traits || []).slice(0, 2).map((trait, i) => (
                            <Chip key={i} label={trait} size="small" variant="outlined" />
                          ))}
                          {(char.traits || []).length > 2 && (
                            <Chip
                              label={`+${char.traits.length - 2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(char.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, char)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredCharacters.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No characters found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredCharacters.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      </TabPanel>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {activeTab === 0 ? 'Story Details' : 'Character Details'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedItem && activeTab === 0 && (
            <Grid container spacing={3}>
              {selectedItem.cover_image_url && (
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardMedia
                      component="img"
                      image={selectedItem.cover_image_url}
                      alt="Cover"
                      sx={{ height: 200, objectFit: 'cover' }}
                    />
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={selectedItem.cover_image_url ? 8 : 12}>
                <Typography variant="h6" gutterBottom>
                  {selectedItem.title || 'Untitled'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created: {new Date(selectedItem.created_at).toLocaleString()}
                </Typography>
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2">Themes:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {(selectedItem.themes || []).map((theme, i) => (
                      <Chip key={i} label={theme} size="small" />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2">Setting:</Typography>
                  <Typography variant="body2">{selectedItem.setting || 'Not specified'}</Typography>
                </Box>
                {selectedItem.custom_details && (
                  <Box sx={{ my: 2 }}>
                    <Typography variant="subtitle2">Custom Details:</Typography>
                    <Typography variant="body2">{selectedItem.custom_details}</Typography>
                  </Box>
                )}
              </Grid>
              {selectedItem.story_content && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Story Content:</Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedItem.story_content}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
          {selectedItem && activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                {selectedItem.avatar ? (
                  <Card>
                    <CardMedia
                      component="img"
                      image={selectedItem.avatar}
                      alt={selectedItem.name}
                      sx={{ height: 200, objectFit: 'cover' }}
                    />
                  </Card>
                ) : (
                  <Avatar
                    sx={{ width: 150, height: 150, fontSize: 60, bgcolor: 'primary.main' }}
                  >
                    {selectedItem.name?.[0]?.toUpperCase() || 'C'}
                  </Avatar>
                )}
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  {selectedItem.name || 'Unnamed'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Age</Typography>
                    <Typography>{selectedItem.age || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Gender</Typography>
                    <Typography>{selectedItem.gender || '-'}</Typography>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">Traits</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {(selectedItem.traits || []).map((trait, i) => (
                      <Chip key={i} label={trait} size="small" />
                    ))}
                  </Box>
                </Box>
                {selectedItem.backstory && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">Backstory</Typography>
                    <Typography variant="body2">{selectedItem.backstory}</Typography>
                  </Box>
                )}
                {selectedItem.appearance && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">Appearance</Typography>
                    <Typography variant="body2">{selectedItem.appearance}</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete this {activeTab === 0 ? 'story' : 'character'}?
          </Typography>
          {selectedItem && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {activeTab === 0 ? selectedItem.title : selectedItem.name} (ID: {selectedItem.id})
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminContent;
